import { createSlice, PayloadAction, nanoid } from "@reduxjs/toolkit";
import { RootState } from ".";
import { WatchlistID, Entity, InsightID, Insight, Task } from "../types/data";
import genInsights from "../utils/genData";
import { randomElement } from "../utils";
import api from "../api";
import { BottomSheet, State } from "../types/redux";
import { calculateStories, ENTITIES } from "../utils/data";

const insights = genInsights;

const initialState: State = {
  entity: "public",
  user: {
    name: randomElement(["Janette", "James", "Mario"]),
    image: randomElement([
      "https://images.unsplash.com/photo-1663691219171-93494f63b5c9?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=697&q=80",
      "https://images.unsplash.com/photo-1597223557154-721c1cecc4b0?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=880&q=80",
      "https://images.unsplash.com/photo-1663743629961-c6134ef1afc9?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=686&q=80",
    ]),
  },
  insights,
  stories: { viewing: undefined, data: calculateStories(insights) },
  watchlist: [],
  bottomSheet: undefined,
};

const mainSlice = createSlice({
  name: "main",
  initialState,
  reducers: {
    setUsername: (state, action: PayloadAction<string>) => {
      state.user.name = action.payload;
    },
    loadData: (state, action: PayloadAction<string>) => {
      const data = api(action.payload);
      if (data) {
        state.user = data.user;
        state.bottomSheet = data.bottomSheet;
        state.entity = data.entity;
        state.insights = data.insights;
        state.watchlist = data.watchlist;
        state.stories.data = calculateStories(data.insights);
      }
    },
    setBottomSheet: (state, action: PayloadAction<BottomSheet | undefined>) => {
      state.bottomSheet = action.payload;
    },
    setEntity: (state, action: PayloadAction<Entity>) => {
      state.entity = action.payload;
    },
    toggleWatchlistID: (state, action: PayloadAction<InsightID>) => {
      const watchlistIndex = state.watchlist.findIndex(
        (watchlistInsight) =>
          watchlistInsight.insightID === action.payload &&
          watchlistInsight.entity === state.entity
      );

      if (watchlistIndex === -1)
        state.watchlist.push({
          id: nanoid(),
          insightID: action.payload,
          entity: state.entity,
        });
      else state.watchlist.splice(watchlistIndex, 1);
    },
    setVisibilityTarget: (
      state,
      action: PayloadAction<{ id: InsightID; target: number }>
    ) => {
      const index = state.insights.findIndex((i) => i.id === action.payload.id);
      const visibilityIndex = state.insights[index].visibilities.findIndex(
        (v) => v.entity === state.entity
      );

      state.insights[index].visibilities[visibilityIndex].target =
        action.payload.target;
    },
    setViewingStory: (state, action: PayloadAction<Entity | undefined>) => {
      if (action.payload) {
        const index = state.stories.data.findIndex(
          (s) => s.entity === action.payload
        );

        state.stories.data[index].viewed = true;
      }

      state.stories.viewing = action.payload;
    },
  },
});

export const {
  setUsername,
  loadData,
  setBottomSheet,
  toggleWatchlistID,
  setVisibilityTarget,
  setEntity,
  setViewingStory,
} = mainSlice.actions;

export default mainSlice.reducer;

//#region Selectors
// Get user
export const selectUser = (state: RootState) => state.main.user;

// Get currently selected entity
export const selectEntity = (state: RootState) => state.main.entity;

// Get all insights
export const selectInsights = (state: RootState) => state.main.insights;

// Get insight with specific entity
export const selectInsight = (state: RootState, id: InsightID) =>
  state.main.insights.find((i) => i.id === id);

// Get insight with specific entity
export const selectInsightWithEntity = (state: RootState, id: InsightID) => {
  // Get insight
  const insight = state.main.insights.find((i) => i.id === id);
  if (!insight) return;

  // Get visibility based on current entity
  const entityVisibility = insight.visibilities.find(
    (v) => v.entity === state.main.entity
  );
  if (!entityVisibility) return;

  return { insight, entityVisibility };
};

// Get all insights in watchlist
export const selectWatchlistInsights = (state: RootState) =>
  state.main.watchlist
    .map((wi) => {
      const insight = state.main.insights.find((i) => wi.insightID === i.id);
      if (!insight) return;

      return {
        ...wi,
        insight,
      };
    })
    .filter((i) => i !== undefined) as {
    id: WatchlistID;
    insightID: InsightID;
    insight: Insight;
    entity: Entity;
  }[];

// Get insight in watchlist
export const selectWatchlistInsight = (state: RootState, id: WatchlistID) => {
  const watchlistInsight = state.main.watchlist.find((i) => i.id === id);
  const insight = state.main.insights.find(
    (i) => i.id === watchlistInsight?.insightID
  );

  if (!watchlistInsight || !insight) return;

  return {
    ...watchlistInsight,
    insight,
  };
};

// Get insight in watchlist
export const selectInWatchlist = (state: RootState, id: InsightID): boolean =>
  state.main.watchlist.findIndex(
    (w) => w.insightID === id && w.entity === state.main.entity
  ) === -1
    ? false
    : true;

// Get all tasks
export const selectTasks = (state: RootState): Task[] => {
  let tasks: Task[] = [];
  state.main.insights.forEach((i) => tasks.push(...i.tasks));

  return tasks;
};

// Get all stories
export const selectStories = (state: RootState) => state.main.stories;

// Get whether story has been viewed or not
export const selectStoryViewed = (state: RootState, entity: Entity) =>
  state.main.stories.data.find((s) => s.entity === entity)?.viewed || false;
//#endregion
